/* eslint-disable @typescript-eslint/no-misused-promises */
import { useLocalState } from '../../../ChatPage/hooks/useLocalState';
import { getCOSConfig } from '../../../service/service';
import { useRef, useCallback } from 'react';

interface ICOSConfig {
  region: string;
  bucket: string;
  session_token: string;
  tmp_secret_id: string;
  tmp_secret_key: string;
}

interface ICachedCOSConfig extends ICOSConfig {
  // 添加签名时间信息
  signatureStartTime: number;
  signatureExpiryTime: number;
  configFetchTime: number; // 配置获取时间
}

// 55分钟，后端设置为1小时，前端有效时长比后端（实际让后端初步看是32h，但是之前代码都是按1h来计算），结合之前aigc代码这里设置55分钟，防止出现后端超时了，前端还没超时的情况
const DURATION = 1 * 60 * 60 - 5 * 60;

// 全局 COS 配置缓存键
const COS_CONFIG_KEY = '__dba_cos_config__';

// 防抖延迟时间（毫秒）
const DEBOUNCE_DELAY = 300;

/**
 * COS 配置管理 Hook
 * 负责 COS 配置的获取、缓存和过期管理
 */
export const useCOSConfig = () => {
  // 使用全局状态管理 COS 配置
  const [cachedConfig, setCachedConfig] = useLocalState<ICachedCOSConfig | null>(COS_CONFIG_KEY, null);
  
  // 防抖相关状态
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingPromiseRef = useRef<Promise<ICachedCOSConfig> | null>(null);

  // 检查配置是否过期
  const isConfigExpired = (config: ICachedCOSConfig): boolean => {
    const now = Math.floor(Date.now() / 1000);
    return now >= config.signatureExpiryTime;
  };

  // 实际的配置获取逻辑
  const fetchConfig = async (): Promise<ICachedCOSConfig> => {
    const now = Math.floor(Date.now() / 1000);
    
    try {
      const newConfig = await getCOSConfig();
      const startTime = now;
      const expiryTime = startTime + DURATION;
      
      const cachedConfigData: ICachedCOSConfig = {
        ...newConfig,
        signatureStartTime: startTime,
        signatureExpiryTime: expiryTime,
        configFetchTime: now,
      };
      
      // 更新全局状态
      setCachedConfig(cachedConfigData);

      return cachedConfigData;
    } catch (error) {
      console.error('获取 COS 配置失败:', error);
      throw error;
    }
  };

  // 获取或刷新 COS 配置（带防抖）
  const getOrRefreshConfig = useCallback(async (): Promise<ICachedCOSConfig> => {
    // const now = Math.floor(Date.now() / 1000);
    
    // 如果配置存在且签名未过期且未即将过期，直接返回
    if (cachedConfig && !isConfigExpired(cachedConfig)) {
      return cachedConfig;
    }

    // 如果已经有正在进行的请求，返回该 Promise
    if (pendingPromiseRef.current) {
      return pendingPromiseRef.current;
    }

    // 清除之前的防抖定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 创建新的 Promise 并缓存
    pendingPromiseRef.current = new Promise<ICachedCOSConfig>((resolve, reject) => {
      debounceTimerRef.current = setTimeout(async () => {
        try {
          const result = await fetchConfig();
          resolve(result);
        } catch (error) {
          reject(error);
        } finally {
          // 清除缓存的 Promise
          pendingPromiseRef.current = null;
        }
      }, DEBOUNCE_DELAY);
    });

    return pendingPromiseRef.current;
  }, [cachedConfig]);

  // 清除配置缓存（用于强制刷新）
  const clearConfig = useCallback((): void => {
    // 清除防抖定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    
    // 清除待处理的 Promise
    pendingPromiseRef.current = null;
    
    setCachedConfig(null);
  }, []);

  // 检查当前配置状态
  const getConfigStatus = useCallback(() => {
    if (!cachedConfig) {
      return { hasConfig: false, isExpired: true };
    }
    
    return {
      hasConfig: true,
      isExpired: isConfigExpired(cachedConfig),
      expiryTime: cachedConfig.signatureExpiryTime,
      remainingTime: Math.max(0, cachedConfig.signatureExpiryTime - Math.floor(Date.now() / 1000)),
    };
  }, [cachedConfig]);

  return {
    // 配置数据
    cachedConfig,
    // 配置管理方法
    getOrRefreshConfig,
    clearConfig,
    // 状态检查方法
    isConfigExpired: () => cachedConfig ? isConfigExpired(cachedConfig) : true,
    getConfigStatus,
  };
};

export default useCOSConfig;
